import { heatsRepository } from "@/repositories/heats/heats.repository";
import { ladlesRepository } from "@/repositories/roots/ladlesRepository";
import { RootState } from "@/store";
import { setLTError } from "@/store/common/common.reducer";
import { getError } from "@/utils/get-error";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Location } from "../../models/ladle-plant-overview.dto";

type ChangeRootLocationState = {
	sourceLocation?: Location;
	targetLocation?: Location;
	sourceLocationWithClick?: Location;
	targetLocationWithClick?: Location;
	isSubmitLoading?: boolean;
	rootPosition?: {
		x: number;
		y: number;
	};
	isRootDragging?: boolean;
	error?: string;
};
const initialState: ChangeRootLocationState = {};

const handleLocationChange = async (
	sourceLocation: Location,
	targetLocation: Location,
) => {
	try {
		const locationId = targetLocation.locationId;
		const heatId = sourceLocation.contentId;
		const rootId = sourceLocation.rootId;

		let resp;
		if (rootId === null && heatId !== null) {
			resp = await heatsRepository.changeHeatLocation(heatId!, locationId);
		} else {
			resp = await ladlesRepository.changeRootLocation({
				locationId,
				rootId,
			});
		}
		return { success: true, result: resp.result };
	} catch (error) {
		const errorMessage = getError(error);
		return { success: false, error: errorMessage };
	}
};

export const submitRootLocationChange = createAsyncThunk(
	"@plantoverview/submitChangeRootLocation",
	async (_, { getState, fulfillWithValue, rejectWithValue, dispatch }) => {
		try {
			const state = getState() as RootState;
			const response = await handleLocationChange(
				state.changeRootLocation.sourceLocation!,
				state.changeRootLocation.targetLocation!,
			);

			if (response.success) {
				return fulfillWithValue(response.result);
			} else {
				dispatch(setLTError(response.error));
				return rejectWithValue(response.error);
			}
		} catch (error) {
			const errorMessage = getError(error);
			dispatch(setLTError(errorMessage));
			return rejectWithValue(errorMessage);
		}
	},
);

export const submitRootLocationChangeWithClick = createAsyncThunk(
	"@plantoverview/submitRootLocationChangeWithClick",
	async (_, { getState, fulfillWithValue, rejectWithValue, dispatch }) => {
		try {
			const state = getState() as RootState;
			const response = await handleLocationChange(
				state.changeRootLocation.sourceLocationWithClick!,
				state.changeRootLocation.targetLocationWithClick!,
			);

			if (response.success) {
				return fulfillWithValue(response.result);
			} else {
				dispatch(setLTError(response.error));
				return rejectWithValue(response.error);
			}
		} catch (error) {
			const errorMessage = getError(error);
			dispatch(setLTError(errorMessage));
			return rejectWithValue(errorMessage);
		}
	},
);

export const changeRootLocationReducer = createSlice({
	name: "@plantoverview/change-root-location",
	initialState,
	reducers: {
		setRoot: (state, action) => {
			state.sourceLocation = action.payload.root;
			state.targetLocation = undefined;
			state.isSubmitLoading = false;
		},
		setTarget: (state, action) => {
			state.targetLocation = action.payload.targetLocation;
			state.isSubmitLoading = false;
		},
		resetRootAndTarget: (state, action) => {
			state.sourceLocation = undefined;
			state.targetLocation = undefined;
			state.isSubmitLoading = false;
		},
		setRootPosition: (state, action) => {
			state.rootPosition = action.payload;
		},
		setIsRootDragging: (state, action) => {
			state.isRootDragging = action.payload;
		},
		setTargetWithClick(state, action) {
			if (
				state.targetLocationWithClick &&
				state.targetLocationWithClick.locationId === action.payload.locationId
			) {
				state.targetLocationWithClick = undefined;
			} else {
				state.targetLocationWithClick = action.payload;
			}
		},
		setSourceWithClick(state, action) {
			state.sourceLocationWithClick = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(submitRootLocationChange.pending, (state, action) => {
				state.isSubmitLoading = true;
				state.error = undefined;
			})
			.addCase(submitRootLocationChange.fulfilled, (state, action) => {
				state.sourceLocation = undefined;
				state.targetLocation = undefined;
				state.isSubmitLoading = false;
				state.targetLocationWithClick = undefined;
				state.sourceLocationWithClick = undefined;
				state.error = undefined;
			})
			.addCase(submitRootLocationChange.rejected, (state, action) => {
				state.isSubmitLoading = false;
				state.error = action.payload as string;
			})
			.addCase(submitRootLocationChangeWithClick.pending, (state, action) => {
				state.isSubmitLoading = true;
				state.error = undefined;
			})
			.addCase(submitRootLocationChangeWithClick.fulfilled, (state, action) => {
				state.sourceLocation = undefined;
				state.targetLocation = undefined;
				state.isSubmitLoading = false;
				state.targetLocationWithClick = undefined;
				state.sourceLocationWithClick = undefined;
				state.error = undefined;
			})
			.addCase(submitRootLocationChangeWithClick.rejected, (state, action) => {
				state.isSubmitLoading = false;
				state.error = action.payload as string;
			});
	},
});

export const {
	setRoot,
	setTarget,
	setTargetWithClick,
	setSourceWithClick,
	setIsRootDragging,
} = changeRootLocationReducer.actions;
